import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Download, Copy, FileText, FileType, Edit, Save, TrendingUp, X, Lightbulb } from "lucide-react";
import { EDUCATION_CONFIG } from "@/components/EducationRequirements";

// Helper to get field descriptions from education_details
const getEducationFieldDescriptions = (educationDetails) => {
  if (!educationDetails || educationDetails.track !== "general_academic") return [];
  
  const descriptions = [];
  const allFields = new Set([
    ...(educationDetails.required?.fields || []),
    ...(educationDetails.preferred?.fields || [])
  ]);
  
  allFields.forEach(fieldValue => {
    if (fieldValue === "related_field") return;
    const field = EDUCATION_CONFIG.general_academic.fields.find(f => f.value === fieldValue);
    if (field) {
      descriptions.push({
        field: field.label,
        description: field.description
      });
    }
  });
  
  return descriptions;
};

export default function RequisitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);
  
  // Refs for auto-scrolling when adding new items
  const responsibilitiesRef = useRef(null);
  const skillsRef = useRef(null);
  const preferredSkillsRef = useRef(null);
  
  // Inline editing states
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({
    title: "",
    description: "",
    requirements: [],
    skills: [],
    preferred_skills: [],
    location_benefits: "",
    basic_qualifications: "",
    preferred_qualifications: "",
    education: "",
    job_type: "Salaried",
    experience_years: "",
    salary_range: "",
    location: "",
    travel: "",
    relocation_provided: "",
    us_citizenship: [],
    security_clearance: "",
    shift_coverage: "",
    benefits_offered: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRequisition();
  }, [id]);

  const fetchRequisition = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}`);
      setRequisition(response.data);
      setNewName(response.data.name || response.data.title);
      // Initialize edited data
      setEditedData({
        title: response.data.title,
        description: response.data.description,
        requirements: response.data.requirements || [],
        skills: response.data.skills || [],
        preferred_skills: response.data.preferred_skills || [],
        location_benefits: response.data.location_benefits || "",
        basic_qualifications: response.data.basic_qualifications || "",
        preferred_qualifications: response.data.preferred_qualifications || "",
        education: response.data.education || "",
        job_type: response.data.job_type || "Salaried",
        experience_years: response.data.experience_years || "",
        salary_range: response.data.salary_range || "",
        location: response.data.location || "",
        company_brand: response.data.company_brand || "",
        travel: response.data.travel || "",
        relocation_provided: response.data.relocation_provided || "",
        us_citizenship: response.data.us_citizenship || [],
        security_clearance: response.data.security_clearance || "",
        shift_coverage: response.data.shift_coverage || "",
        benefits_offered: response.data.benefits_offered || []
      });
    } catch (error) {
      toast.error("Failed to load requisition");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this requisition?")) return;
    
    try {
      await apiClient.delete(`/requisitions/${id}`);
      toast.success("Requisition deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete requisition");
    }
  };

  const handleEdit = () => {
    // Navigate to builder with requisition data for editing
    console.log("Edit Input clicked - navigating to /builder?edit=" + id);
    navigate(`/builder?edit=${id}`);
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setSavingName(true);
    try {
      // Check for duplicate requisition name
      const response = await apiClient.get("/requisitions");
      const existingReqs = response.data;
      const duplicateName = existingReqs.some(
        req => req.name.toLowerCase() === newName.trim().toLowerCase() 
          && req.id !== id // Allow same name for current requisition
      );
      
      if (duplicateName) {
        toast.error(
          <div>
            <strong>Duplicate Name</strong>
            <p className="text-sm mt-1">A requisition with this name already exists.</p>
            <p className="text-sm mt-1">Please choose a different name.</p>
          </div>,
          { duration: 5000 }
        );
        setSavingName(false);
        return;
      }

      await apiClient.put(`/requisitions/${id}/rename`, { name: newName });
      setRequisition({ ...requisition, name: newName });
      setEditingName(false);
      toast.success("Name updated successfully!");
    } catch (error) {
      toast.error("Failed to update name");
    } finally {
      setSavingName(false);
    }
  };


  const handleToggleEdit = () => {
    if (editMode) {
      // Cancel editing - reset to original data
      setEditedData({
        title: requisition.title,
        description: requisition.description,
        requirements: requisition.requirements || [],
        skills: requisition.skills || [],
        preferred_skills: requisition.preferred_skills || [],
        location_benefits: requisition.location_benefits || "",
        basic_qualifications: requisition.basic_qualifications || "",
        preferred_qualifications: requisition.preferred_qualifications || "",
        education: requisition.education || "",
        job_type: requisition.job_type || "Salaried",
        experience_years: requisition.experience_years || "",
        salary_range: requisition.salary_range || "",
        location: requisition.location || "",
        company_brand: requisition.company_brand || "",
        travel: requisition.travel || "",
        relocation_provided: requisition.relocation_provided || "",
        us_citizenship: requisition.us_citizenship || [],
        security_clearance: requisition.security_clearance || "",
        shift_coverage: requisition.shift_coverage || "",
        benefits_offered: requisition.benefits_offered || []
      });
      // Reset name as well
      setNewName(requisition.name || requisition.title);
    } else {
      // Entering edit mode - load current data
      setEditedData({
        title: requisition.title,
        description: requisition.description,
        requirements: requisition.requirements || [],
        skills: requisition.skills || [],
        preferred_skills: requisition.preferred_skills || [],
        location_benefits: requisition.location_benefits || "",
        basic_qualifications: requisition.basic_qualifications || "",
        preferred_qualifications: requisition.preferred_qualifications || "",
        education: requisition.education || "",
        job_type: requisition.job_type || "Salaried",
        experience_years: requisition.experience_years || "",
        salary_range: requisition.salary_range || "",
        location: requisition.location || "",
        company_brand: requisition.company_brand || "",
        travel: requisition.travel || "",
        relocation_provided: requisition.relocation_provided || "",
        us_citizenship: requisition.us_citizenship || [],
        security_clearance: requisition.security_clearance || "",
        shift_coverage: requisition.shift_coverage || "",
        benefits_offered: requisition.benefits_offered || []
      });
    }
    setEditMode(!editMode);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Check for duplicate requisition name in library
      const allLibraryReqs = await apiClient.get("/requisitions/library");
      const duplicateName = allLibraryReqs.data.some(
        req => req.name.toLowerCase() === newName.trim().toLowerCase() 
          && req.id !== id // Allow same name for current requisition
      );
      
      if (duplicateName) {
        toast.error(
          <div>
            <strong>Duplicate Name</strong>
            <p className="text-sm mt-1">A requisition with this name already exists in your library.</p>
            <p className="text-sm mt-1">Please edit the <strong>Requisition Name</strong> field above and try saving again.</p>
          </div>,
          { duration: 6000 }
        );
        setSaving(false);
        return;
      }

      // Clean up requirements, skills, and preferred_skills by removing empty lines before saving
      const cleanedRequirements = editedData.requirements.filter(line => line.trim());
      const cleanedSkills = editedData.skills.filter(skill => skill.trim());
      const cleanedPreferredSkills = editedData.preferred_skills.filter(skill => skill.trim());
      
      // Save to library (not sandbox)
      await apiClient.post('/requisitions/library', {
        name: newName,
        title: editedData.title,
        description: editedData.description,
        requirements: cleanedRequirements,
        skills: cleanedSkills,
        preferred_skills: cleanedPreferredSkills,
        education: editedData.education,
        experience_years: editedData.experience_years,
        salary_range: editedData.salary_range,
        location: editedData.location || "",
        location_benefits: editedData.location_benefits || "",
        company_brand: editedData.company_brand || "",
        travel: editedData.travel || "",
        relocation_provided: editedData.relocation_provided || "",
        us_citizenship: editedData.us_citizenship || [],
        security_clearance: editedData.security_clearance || "",
        job_type: editedData.job_type || "",
        shift_coverage: editedData.shift_coverage || "",
        benefits_offered: editedData.benefits_offered || []
      });
      
      setEditMode(false);
      toast.success("Saved to Req Library!");
      // Optionally navigate to library
      navigate("/library");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to save to library");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/export/json`);
      await navigator.clipboard.writeText(response.data.content);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleSaveToLibrary = async () => {
    try {
      // Save requisition output to library (without input parameters)
      await apiClient.post('/requisitions/library', {
        name: requisition.name || requisition.title,
        title: requisition.title,
        description: requisition.description,
        requirements: requisition.requirements || [],
        skills: requisition.skills || [],
        preferred_skills: requisition.preferred_skills || [],
        education: requisition.education || "",
        experience_years: requisition.experience_years || "",
        salary_range: requisition.salary_range || "",
        location: requisition.location || "",
        location_benefits: requisition.location_benefits || "",
        company_brand: requisition.company_brand || "",
        travel: requisition.travel || "",
        relocation_provided: requisition.relocation_provided || "",
        us_citizenship: requisition.us_citizenship || [],
        security_clearance: requisition.security_clearance || "",
        job_type: requisition.job_type || "",
        shift_coverage: requisition.shift_coverage || "",
        benefits_offered: requisition.benefits_offered || []
      });
      toast.success("Saved to Req Library!");
      // Optionally navigate to library
      // navigate("/library");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to save to library");
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/export/word`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Word document downloaded!");
    } catch (error) {
      toast.error("Failed to export Word document");
    }
  };

  const handleExportText = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/export/text`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Text file downloaded!");
    } catch (error) {
      toast.error("Failed to export text file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!requisition) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="return-to-req-desktop-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Req Desktop
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/library")}
            >
              Go to Req Library
            </Button>
          </div>
          
          {/* Name Display/Edit */}
          <div className="flex items-center justify-between mb-4">
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="max-w-md"
                  placeholder="Enter requisition name"
                />
                <Button
                  size="sm"
                  onClick={handleSaveName}
                  disabled={savingName}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingName(false);
                    setNewName(requisition.name || requisition.title);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-1">
                <h1 className="text-2xl font-bold text-slate-900">
                  {requisition.name || requisition.title}
                </h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingName(true)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            {/* Row 1: Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
            {editMode ? (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveChanges}
                  disabled={saving}
                  data-testid="save-changes-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save to Req Library"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleEdit}
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleToggleEdit}
                  data-testid="edit-requisition-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit & Save Req Output
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/requisition/${id}/competitive-intelligence`)}
                  data-testid="competitive-intelligence-btn"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Competitive Intelligence
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/requisition/${id}/market-analysis`)}
                  data-testid="market-analysis-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Market Analysis
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  data-testid="edit-input-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Req Input
                </Button>
              </>
            )}
            </div>

            {/* Row 2: Export and management buttons */}
            <div className="flex items-center gap-2 flex-wrap">
            {!editMode && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyToClipboard}
                        data-testid="copy-clipboard-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy to Clipboard</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportText}
                        data-testid="export-text-btn"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Text
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export as Text File</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportPDF}
                        data-testid="export-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export as PDF</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportWord}
                        data-testid="export-word-btn"
                      >
                        <FileType className="w-4 h-4 mr-1" />
                        Word
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export as Word Document</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveToLibrary}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save to Req Library
                </Button>
              </>
            )}

            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              data-testid="delete-requisition-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {editMode ? (
            /* Edit Mode - Card-based layout */
            <>
          {/* Requisition Name */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requisition Name</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter requisition name"
                rows={2}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-24"
              />
            </CardContent>
          </Card>

          {/* Job Title */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job Title</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.title}
                onChange={(e) => setEditedData({...editedData, title: e.target.value})}
                placeholder="Job Title"
                rows={2}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-24"
              />
            </CardContent>
          </Card>

          {/* Company Brand */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Company Brand</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.company_brand || ""}
                onChange={(e) => setEditedData({...editedData, company_brand: e.target.value})}
                placeholder="Company Name"
                rows={2}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-24"
              />
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.description}
                onChange={(e) => setEditedData({...editedData, description: e.target.value})}
                rows={8}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
              />
            </CardContent>
          </Card>

          {/* Key Responsibilities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Key Responsibilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={responsibilitiesRef} className="max-h-64 overflow-y-auto">
                <ul className="list-disc list-outside space-y-2 pl-6">
                  {editedData.requirements.map((req, idx) => {
                    // Skip items that are just '**' or undefined/null, but KEEP empty strings for new entries
                    if (req === '**' || req === undefined || req === null) return null;
                    
                    return (
                      <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                        <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                          <textarea
                            value={req}
                            onChange={(e) => {
                              const newReqs = [...editedData.requirements];
                              newReqs[idx] = e.target.value;
                              setEditedData({...editedData, requirements: newReqs});
                            }}
                            className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                            placeholder="Enter responsibility"
                            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                            onFocus={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onInput={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                          <button
                            onClick={() => {
                              const newReqs = [...editedData.requirements];
                              newReqs.splice(idx, 1);
                              setEditedData({...editedData, requirements: newReqs});
                            }}
                            className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <button
                onClick={() => {
                  setEditedData({
                    ...editedData, 
                    requirements: [...editedData.requirements, '']
                  });
                  // Auto-scroll to show new item
                  setTimeout(() => {
                    if (responsibilitiesRef.current) {
                      responsibilitiesRef.current.scrollTop = responsibilitiesRef.current.scrollHeight;
                    }
                  }, 50);
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Add Responsibility
              </button>
            </CardContent>
          </Card>

          {/* Required Skills */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Required Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={skillsRef} className="max-h-64 overflow-y-auto">
                <ul className="list-disc list-outside space-y-2 pl-6">
                  {editedData.skills.map((skill, idx) => {
                    // Skip items that are just '**' or undefined/null, but KEEP empty strings for new entries
                    if (skill === '**' || skill === undefined || skill === null) return null;
                    
                    return (
                      <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                        <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                          <textarea
                            value={skill}
                            onChange={(e) => {
                              const newSkills = [...editedData.skills];
                              newSkills[idx] = e.target.value;
                              setEditedData({...editedData, skills: newSkills});
                            }}
                            className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                            placeholder="Enter skill"
                            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                            onFocus={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onInput={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                          <button
                            onClick={() => {
                              const newSkills = [...editedData.skills];
                              newSkills.splice(idx, 1);
                              setEditedData({...editedData, skills: newSkills});
                            }}
                            className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <button
                onClick={() => {
                  setEditedData({
                    ...editedData, 
                    skills: [...editedData.skills, '']
                  });
                  // Auto-scroll to show new item
                  setTimeout(() => {
                    if (skillsRef.current) {
                      skillsRef.current.scrollTop = skillsRef.current.scrollHeight;
                    }
                  }, 50);
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Add Skill
              </button>
            </CardContent>
          </Card>

          {/* Preferred Skills */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preferred Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={preferredSkillsRef} className="max-h-64 overflow-y-auto">
                <ul className="list-disc list-outside space-y-2 pl-6">
                  {editedData.preferred_skills.map((skill, idx) => {
                    // Skip items that are just '**' or undefined/null, but KEEP empty strings for new entries
                    if (skill === '**' || skill === undefined || skill === null) return null;
                    
                    return (
                      <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                        <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                          <textarea
                            value={skill}
                            onChange={(e) => {
                              const newSkills = [...editedData.preferred_skills];
                              newSkills[idx] = e.target.value;
                              setEditedData({...editedData, preferred_skills: newSkills});
                            }}
                            className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                            placeholder="Enter preferred skill"
                            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                            onFocus={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onInput={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                          <button
                            onClick={() => {
                              const newSkills = [...editedData.preferred_skills];
                              newSkills.splice(idx, 1);
                              setEditedData({...editedData, preferred_skills: newSkills});
                            }}
                            className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <button
                onClick={() => {
                  setEditedData({
                    ...editedData, 
                    preferred_skills: [...editedData.preferred_skills, '']
                  });
                  // Auto-scroll to show new item
                  setTimeout(() => {
                    if (preferredSkillsRef.current) {
                      preferredSkillsRef.current.scrollTop = preferredSkillsRef.current.scrollHeight;
                    }
                  }, 50);
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Add Preferred Skill
              </button>
            </CardContent>
          </Card>

          {/* Basic Qualifications and Preferred Qualifications sections removed - used only as input for AI generation */}

          {/* Experience - moved before Education */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Experience</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.experience_years}
                onChange={(e) => setEditedData({...editedData, experience_years: e.target.value})}
                placeholder="e.g., 5-7 years"
                rows={3}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-32"
              />
            </CardContent>
          </Card>

          {/* Education */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Education</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.education || ""}
                onChange={(e) => setEditedData({...editedData, education: e.target.value})}
                placeholder="Enter education requirements (e.g., Bachelor's degree in Engineering, MBA preferred)"
                rows={3}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-32"
                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              />
            </CardContent>
          </Card>

          {/* Salary Range */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Salary Range</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedData.salary_range}
                onChange={(e) => setEditedData({...editedData, salary_range: e.target.value})}
                placeholder="e.g., $100,000 - $130,000"
                rows={3}
                className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-32"
              />
            </CardContent>
          </Card>

          {/* Additional Details - Parent Card with nested fields */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Travel */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Travel</h3>
                <Textarea
                  value={editedData.travel || ''}
                  onChange={(e) => setEditedData({...editedData, travel: e.target.value})}
                  placeholder="e.g., No travel, Up to 25%"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* Relocation */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Relocation</h3>
                <Textarea
                  value={editedData.relocation_provided || ''}
                  onChange={(e) => setEditedData({...editedData, relocation_provided: e.target.value})}
                  placeholder="e.g., Yes, No, Negotiable"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* US Citizenship */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">US Citizenship</h3>
                <Textarea
                  value={(editedData.us_citizenship || []).join(', ')}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    const citizenshipArray = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
                    setEditedData({...editedData, us_citizenship: citizenshipArray});
                  }}
                  placeholder="e.g., U.S. Citizen, U.S. permanent resident (i.e. Green Card holder)"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* Security Clearance */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Security Clearance</h3>
                <Textarea
                  value={editedData.security_clearance || ''}
                  onChange={(e) => setEditedData({...editedData, security_clearance: e.target.value})}
                  placeholder="e.g., None, Confidential, Secret, Top Secret"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* Job Type */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Job Type</h3>
                <Textarea
                  value={editedData.job_type || ''}
                  onChange={(e) => setEditedData({...editedData, job_type: e.target.value})}
                  placeholder="e.g., Salaried, Hourly, Part Time, Contract"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* Shift */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Shift</h3>
                <Textarea
                  value={editedData.shift_coverage || ''}
                  onChange={(e) => setEditedData({...editedData, shift_coverage: e.target.value})}
                  placeholder="e.g., First Shift (Day), Second Shift (Evening), Third Shift (Night)"
                  rows={2}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>

              {/* Benefits Offered */}
              <div>
                <h3 className="font-semibold text-sm text-slate-600 mb-2">Benefits Offered</h3>
                <Textarea
                  value={(editedData.benefits_offered || []).join(', ')}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    const benefitsArray = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
                    setEditedData({...editedData, benefits_offered: benefitsArray});
                  }}
                  placeholder="e.g., Paid time off, Health insurance, 401(k) matching"
                  rows={3}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap"
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Location</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">City, State</label>
                <Input
                  value={editedData.location || ""}
                  onChange={(e) => setEditedData({...editedData, location: e.target.value})}
                  placeholder="e.g., Huntsville, AL"
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Location Benefits</label>
                <Textarea
                  value={editedData.location_benefits}
                  onChange={(e) => setEditedData({...editedData, location_benefits: e.target.value})}
                  placeholder="Describe location benefits..."
                  rows={4}
                  className="w-full resize-none overflow-y-auto break-words whitespace-pre-wrap max-h-64"
                />
              </div>
            </CardContent>
          </Card>
          </>
        ) : (
            /* Display Mode - Single continuous card (matching create page) */
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="font-bold">Requisition Name: </span>
                  {requisition.name || requisition.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Job Title */}
                <div>
                  <p className="text-slate-900">
                    <span className="font-bold">Job Title: </span>
                    {requisition.title}
                  </p>
                </div>

                {/* Company Brand */}
                {requisition.company_brand && (
                  <div>
                    <p className="text-slate-900">
                      <span className="font-bold">Company Brand: </span>
                      {requisition.company_brand}
                    </p>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-1">Description</h3>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{requisition.description}</p>
                </div>

                {/* Key Responsibilities */}
                {requisition.requirements && requisition.requirements.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Key Responsibilities</h3>
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {requisition.requirements.filter(req => req && req.trim() && req !== '**').map((req, i) => (
                        <li key={i} className="text-sm text-slate-700">{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Required Skills */}
                {requisition.skills && requisition.skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Required Skills</h3>
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {requisition.skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                        <li key={i} className="text-sm text-slate-700">{skill}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preferred Skills */}
                {requisition.preferred_skills && requisition.preferred_skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Preferred Skills</h3>
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {requisition.preferred_skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                        <li key={i} className="text-sm text-slate-700">{skill}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Experience - moved before Education */}
                {requisition.experience_years && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Experience</h3>
                    <div className="text-sm text-slate-700 whitespace-pre-line">{requisition.experience_years}</div>
                  </div>
                )}

                {/* Education */}
                {requisition.education && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Education</h3>
                    <p className="text-sm text-slate-700">{requisition.education}</p>
                    
                    {/* Show field descriptions for academic education */}
                    {requisition.education_details && (() => {
                      const fieldDescriptions = getEducationFieldDescriptions(requisition.education_details);
                      if (fieldDescriptions.length > 0) {
                        return (
                          <div className="mt-2 pl-3 border-l-2 border-slate-200">
                            <p className="text-xs text-slate-500 font-medium mb-1">Fields of Study Include:</p>
                            {fieldDescriptions.map((item, idx) => (
                              <div key={idx} className="text-xs text-slate-600 mb-1">
                                <span className="font-medium">{item.field}:</span>{" "}
                                <span className="text-slate-500">{item.description}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {/* Salary Range */}
                {requisition.salary_range && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Salary Range</h3>
                    <p className="text-sm text-slate-700">{requisition.salary_range}</p>
                  </div>
                )}

                {/* Travel */}
                {requisition.travel && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Travel</h3>
                    <p className="text-sm text-slate-700">{requisition.travel}</p>
                  </div>
                )}

                {/* Additional Details Section */}
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-2">Additional Details</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Relocation:</span> {requisition.relocation_provided || 'Not specified'}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">US Citizenship:</span> {requisition.us_citizenship && requisition.us_citizenship.length > 0 ? requisition.us_citizenship.join(", ") : 'Not specified'}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Security Clearance:</span> {requisition.security_clearance || 'Not specified'}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Job Type:</span> {requisition.job_type || 'Not specified'}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Shift:</span> {requisition.shift_coverage || 'Not specified'}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Benefits Offered:</span> {requisition.benefits_offered && requisition.benefits_offered.length > 0 ? requisition.benefits_offered.join(", ") : 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Location */}
                {(requisition.location || requisition.location_benefits) && (
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Location</h3>
                    {requisition.location && (
                      <p className="text-sm text-slate-700 mb-1">{requisition.location}</p>
                    )}
                    {requisition.location_benefits && (
                      <p className="text-sm text-slate-700">{requisition.location_benefits}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}